import { useAgentChat } from '../../contexts/AgentChatContext';
import { AgentChangesProps } from '../../types/agentChanges';
import { AgentChangesNotification } from './AgentChangesNotification';
import { AgentChatInput } from './AgentChatInput';
import { AgentChatMessages } from './AgentChatMessages';

interface LandingPageAgentChatProps extends AgentChangesProps {
  agentName: string;
  className?: string;
  placeholder?: string;
  sendMessageToIframe?: (message: any) => void;
  isVisualEditMode?: boolean;
  onToggleVisualEdit?: () => void;
}

export function LandingPageAgentChat({
  className = '',
  // eslint-disable-next-line quotes
  placeholder = "Describe what you'd like to change or add...",
  changes = [],
  onChangeClick,
  onAcceptChange,
  onDiscardChange,
  onAcceptAllChanges,
  onDiscardAllChanges,
  isVisualEditMode,
  onToggleVisualEdit,
}: LandingPageAgentChatProps) {
  const {
    getAgentState,
    sendMessage,
    stopStreaming,
    promptContexts,
    setPromptContexts,
    updatePromptContext,
    addPromptContext,
  } = useAgentChat();
  const agentState = getAgentState();

  // Get user message history for arrow-key navigation
  const userMessageHistory = agentState.messages
    .filter((msg) => msg.type === 'user')
    .map((msg) => msg.content)
    .reverse();

  // Get thread ID for file uploads
  const threadId =
    agentState.currentThreadId || agentState.currentRunId || 'default';

  return (
    <div
      className={`flex h-full flex-col ${className}`}
      style={{
        background: 'linear-gradient(180deg, #F7F5FF 0%, #E5EEFF 100%)',
      }}
    >
      {/* Messages Container */}
      <AgentChatMessages
        messages={agentState.messages}
        isWorking={agentState.isWorking}
        isLoading={agentState.isLoading}
        lastUpdated={agentState.lastUpdated}
      />

      {/* Changes Notification Bar (conditionally shown) */}
      {changes.length > 0 &&
        onAcceptChange &&
        onDiscardChange &&
        onAcceptAllChanges &&
        onDiscardAllChanges && (
          <AgentChangesNotification
            disabled={agentState.isLoading || agentState.isStreaming}
            changes={changes}
            onChangeClick={onChangeClick}
            onAcceptChange={onAcceptChange}
            onDiscardChange={onDiscardChange}
            onAcceptAll={onAcceptAllChanges}
            onDiscardAll={onDiscardAllChanges}
          />
        )}

      {/* Input Component */}
      <AgentChatInput
        onSendMessage={sendMessage}
        onStopMessage={stopStreaming}
        placeholder={placeholder}
        disabled={agentState.isLoading}
        isStreaming={agentState.isStreaming}
        className="p-2"
        isVisualEditMode={isVisualEditMode}
        onToggleVisualEdit={onToggleVisualEdit}
        contextTypes={[
          { type: 'file', location: 'dropdown' },
          { type: 'asset', location: 'dropdown' },
          { type: 'element', location: 'inline' },
        ]}
        promptContexts={promptContexts}
        setPromptContexts={setPromptContexts}
        updatePromptContext={updatePromptContext}
        addPromptContext={addPromptContext}
        userMessageHistory={userMessageHistory}
        threadId={threadId}
      />
    </div>
  );
}
